import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Box, Stack, Typography, Collapse } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

/**
 * Admin sidebar.
 *
 * Distinct from the brand sidebar by design — different consumer, different
 * surface area. Dark, dense, dashboard-utility aesthetic. Each top-level
 * entry is either a single page (Overview, Audit) or a collapsible group
 * that holds related pages (Users & Access, Billing, Operations).
 *
 * The route shape is intentionally flat (no `layout` indirection like the
 * brand routes use) — these are mounted directly under `/admin/*` by the
 * platform layout's own <Routes>.
 */

const NAV = [
  { type: 'item', label: 'Overview', path: 'overview' },
  {
    type: 'group',
    label: 'Users & Access',
    children: [
      { label: 'Users',  path: 'users' },
      { label: 'Brands', path: 'brands' },
    ],
  },
  {
    type: 'group',
    label: 'Billing',
    children: [
      { label: 'Plans',         path: 'plans' },
      { label: 'Subscriptions', path: 'subscriptions' },
      { label: 'Earnings',      path: 'earnings' },
    ],
  },
  {
    type: 'group',
    label: 'Operations',
    children: [
      { label: 'Domains',     path: 'domains' },
      { label: 'Errors (7d)', path: 'errors' },
    ],
  },
  { type: 'item', label: 'Audit log', path: 'audit' },
];

const itemSx = (active) => ({
  display: 'block',
  textDecoration: 'none',
  px: '14px',
  py: '8px',
  mx: '8px',
  borderRadius: '8px',
  color: active ? '#fff' : 'rgba(229,231,235,0.72)',
  bgcolor: active ? 'rgba(124,58,237,0.18)' : 'transparent',
  fontSize: '13.5px',
  fontWeight: active ? 600 : 500,
  letterSpacing: '0.01em',
  transition: 'background-color 120ms, color 120ms',
  '&:hover': { bgcolor: active ? 'rgba(124,58,237,0.24)' : 'rgba(255,255,255,0.06)', color: '#fff' },
});

function GroupHeader({ label, open, onToggle }) {
  return (
    <Stack
      direction='row'
      alignItems='center'
      onClick={onToggle}
      sx={{
        px: '14px',
        py: '6px',
        mx: '8px',
        mt: '14px',
        mb: '2px',
        cursor: 'pointer',
        color: 'rgba(156,163,175,0.95)',
        fontSize: '11px',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        userSelect: 'none',
        borderRadius: '6px',
        '&:hover': { color: '#fff' },
      }}
    >
      {open ? <ExpandMoreIcon sx={{ fontSize: 16, mr: '4px' }} /> : <ChevronRightIcon sx={{ fontSize: 16, mr: '4px' }} />}
      {label}
    </Stack>
  );
}

export default function AdminSidebar() {
  const { pathname } = useLocation();
  // All groups default to open. Persist collapse state per-session so the
  // sidebar feels stable across navigations within the admin panel.
  const [open, setOpen] = useState(() => {
    try {
      const stored = sessionStorage.getItem('tpo_admin_nav_open');
      return stored ? JSON.parse(stored) : {};
    } catch { return {}; }
  });
  const toggle = (k) => {
    setOpen((prev) => {
      const next = { ...prev, [k]: prev[k] === false ? true : false };
      try { sessionStorage.setItem('tpo_admin_nav_open', JSON.stringify(next)); } catch (_) { /* ignore */ }
      return next;
    });
  };
  const isOpen = (k) => open[k] !== false; // default-open

  const isActive = (path) => pathname.startsWith(`/admin/${path}`);

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        bottom: 0,
        width: 240,
        bgcolor: '#0F1117',
        borderRight: '1px solid rgba(255,255,255,0.06)',
        display: 'flex',
        flexDirection: 'column',
        zIndex: 1200,
      }}
    >
      <Box sx={{ px: '20px', py: '18px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '15px', letterSpacing: '0.02em' }}>
          TechProOne
        </Typography>
        <Typography sx={{ color: 'rgba(167,139,250,0.95)', fontSize: '11px', fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', mt: '2px' }}>
          Platform admin
        </Typography>
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', py: '10px' }}>
        {NAV.map((entry, i) => {
          if (entry.type === 'item') {
            return (
              <NavLink key={entry.path} to={`/admin/${entry.path}`} style={{ textDecoration: 'none' }}>
                {({ isActive: navActive }) => (
                  <Box sx={itemSx(navActive || isActive(entry.path))}>{entry.label}</Box>
                )}
              </NavLink>
            );
          }
          const o = isOpen(entry.label);
          return (
            <Box key={`g-${i}`}>
              <GroupHeader label={entry.label} open={o} onToggle={() => toggle(entry.label)} />
              <Collapse in={o} unmountOnExit>
                {entry.children.map((c) => (
                  <NavLink key={c.path} to={`/admin/${c.path}`} style={{ textDecoration: 'none' }}>
                    {({ isActive: navActive }) => (
                      <Box sx={{ ...itemSx(navActive || isActive(c.path)), pl: '26px' }}>{c.label}</Box>
                    )}
                  </NavLink>
                ))}
              </Collapse>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
